import { useEffect, useState } from 'react';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/primitives';
import { ApiError } from '@/lib/api';
import { quickUpdateLead, newUpdateKey } from '@/data/leads';
import { diffQuickUpdate, STATUS_OPTIONS, VISIT_APPROACH_OPTIONS, type LeadQuickUpdate, type LeadRecord } from '@/data/leads-model';

/* In-field quick update (owner spec): status, visit approach, next follow-up
 * (+ optional visit date / outcome note) from the card, while standing in the
 * shop. One PATCH per Save with ONE idempotency key per decision: a retry after
 * a flaky mobile connection replays, never double-applies. Only changed fields
 * are sent (diffQuickUpdate). */
export function QuickUpdateSheet({ lead, open, onClose, onSaved }: { lead: LeadRecord | null; open: boolean; onClose: () => void; onSaved: (lead: LeadRecord) => void }) {
  const [draft, setDraft] = useState<LeadQuickUpdate>({});
  const [key, setKey] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (lead && open) {
      setDraft({
        status: lead.status,
        visit_approach: lead.visit_approach ?? null,
        next_action_date: lead.next_action_date ?? null,
        visit_date: lead.visit_date ?? null,
        outcome_note: lead.outcome_note ?? null,
      });
      setKey(newUpdateKey());
      setError(null);
    }
  }, [lead, open]);

  if (!lead) return null;
  const changes = diffQuickUpdate(lead, draft);
  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (!dirty) { onClose(); return; }
    setBusy(true); setError(null);
    try {
      const res = await quickUpdateLead(lead.lead_ref, changes, key);
      onSaved(res.lead);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save — check your connection and tap Save again (safe to retry).');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={<span className="truncate">{lead.business_name ?? lead.lead_ref}</span>}>
      <div className="space-y-4 p-4" data-testid="quick-update">
        <Field label="Sales status">
          <select className={selectCls} value={draft.status ?? ''} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Visit approach">
          <select className={selectCls} value={draft.visit_approach ?? ''} onChange={(e) => setDraft({ ...draft, visit_approach: e.target.value || null })}>
            <option value="">Not set</option>
            {VISIT_APPROACH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Next follow-up">
            <input type="date" className={selectCls} value={draft.next_action_date ?? ''} onChange={(e) => setDraft({ ...draft, next_action_date: e.target.value || null })} />
          </Field>
          <Field label="Visit date">
            <input type="date" className={selectCls} value={draft.visit_date ?? ''} onChange={(e) => setDraft({ ...draft, visit_date: e.target.value || null })} />
          </Field>
        </div>
        <Field label="Outcome / current pain">
          <textarea rows={3} className={cn2(selectCls, 'h-auto py-2')} placeholder="What did you hear on the visit?" value={draft.outcome_note ?? ''} onChange={(e) => setDraft({ ...draft, outcome_note: e.target.value || null })} />
        </Field>
        {error ? <p className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">{error}</p> : null}
        <div className="flex gap-2 pt-1">
          <Button variant="primary" className="h-11 flex-1" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : dirty ? 'Save' : 'Nothing changed'}
          </Button>
          <Button variant="secondary" className="h-11" onClick={onClose} disabled={busy}>Cancel</Button>
        </div>
        <p className="text-[11px] text-text-tertiary">Saved changes are audit-logged on the lead timeline. Re-uploading the workbook later never overwrites what you set here.</p>
      </div>
    </Drawer>
  );
}

const selectCls = 'h-11 w-full rounded-md border border-border-default bg-surface px-3 text-sm text-text-primary';
function cn2(...a: string[]): string { return a.join(' '); }
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wide text-text-tertiary">{label}</span>
      {children}
    </label>
  );
}
